import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { formatNumber } from "../../utils/formatNumber";
import AlertModal from "../AlertModal";
import { useAccount, useSecretNetworkClient } from "../../models/account";
import {
  customerDeposit,
  enterP2PDeal,
  fetchSnipTokenBalance,
  P2PConfig,
  P2PPost,
} from "../../models/p2p";
import PostCard from "./PostCard";
import useIsMobile from "../../utils/useIsMobile";
import decodeTxMsg from "../../utils/decodeTxMsg";

interface Props {
  open: boolean;
  onClose: () => void;
  post?: P2PPost;
  config?: P2PConfig;
  refetch?: () => void;
}

const EnterDealModal: FC<Props> = ({
  open,
  onClose,
  post,
  config,
  refetch,
}) => {
  const secretjs = useSecretNetworkClient();
  const isMobile = useIsMobile();
  const { account } = useAccount();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [amount, setAmount] = useState("");

  const [max, setMax] = useState(0);

  useEffect(() => {
    if (open && account.address && secretjs) {
      fetchSnipTokenBalance(
        {
          contract_address: config?.deal_token?.address || "",
          code_hash: config?.deal_token?.code_hash || "",
          address: account.address,
          chain_id: account.chainId,
          decimals: config?.decimals || 6,
        },
        secretjs
      ).then(setMax);
    }
  }, [open, config, account]);

  const message =
    amount &&
    Number(amount) > (post?.amount || 0) / 10 ** (config?.decimals || 6)
      ? "Exceed max amount"
      : amount &&
        Number(amount) < (post?.min_amount || 0) / 10 ** (config?.decimals || 6)
      ? "Less than minimum amount"
      : "";

  useEffect(() => {
    if (refetch && (success || error)) {
      refetch();
    }
  }, [refetch, success, error]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!secretjs) {
              return;
            }
            try {
              setError("");
              setLoading(true);
              const paramsAmount = (
                Number(amount) *
                10 ** (config?.decimals || 6)
              ).toFixed();
              const tx = await enterP2PDeal(
                {
                  contract_address: config?.contract_address || "",
                  code_hash: config?.code_hash || "",
                  address: account.address,
                  post_id: post?.post_id || "",
                  amount: paramsAmount,
                },
                secretjs
              );
              if (post?.is_dealer_buy) {
                const txMsg = decodeTxMsg(tx);
                await customerDeposit(
                  {
                    contract_address: config?.contract_address || "",
                    token_address: config?.deal_token?.address || "",
                    token_code_hash: config?.deal_token?.code_hash || "",
                    address: account?.address || "",
                    deal_id: txMsg?.deal_stage_process?.deal_id || "",
                    amount: paramsAmount,
                  },
                  secretjs!
                );
              }
              setLoading(false);
              setSuccess(`You have successfully entered a trade.`);
              onClose();
            } catch (err: any) {
              setLoading(false);
              setError(err.message);
            }
          }}
        >
          <DialogTitle>
            {!post?.is_dealer_buy ? "Buy" : "Sell"} {config?.name}
          </DialogTitle>
          <DialogContent>
            <PostCard config={config} post={post} isMobile={isMobile} />
            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">Amount</Typography>
              {post?.is_dealer_buy && (
                <Typography variant="body2">
                  <Link
                    underline="hover"
                    sx={{ cursor: "pointer", ml: 1 }}
                    onClick={() => setAmount(String(max))}
                  >
                    Available: {formatNumber(max || 0)} {config?.name}
                  </Link>
                </Typography>
              )}
            </Stack>
            <TextField
              value={amount}
              error={!!error && error !== "empty"}
              onChange={(e) => {
                if (
                  !e.target.value.match(/[^.0-9]/) &&
                  (e.target.value.match(/\./g) || []).length < 2
                ) {
                  setAmount(e.target.value);
                }
              }}
              placeholder="0.0"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">{config?.name}</InputAdornment>
                ),
              }}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-end"
              my={3}
            >
              <Typography variant="body1">
                {!post?.is_dealer_buy ? "You spend" : "You receive"}{" "}
                <b>
                  {formatNumber(
                    Number(amount) *
                      ((post?.settle_price || 0) /
                        10 ** (config?.decimals || 6))
                  )}
                </b>{" "}
                {post?.settle_currency.currency} for{" "}
                <b>{formatNumber(Number(amount))}</b> {config?.name}
              </Typography>
            </Stack>
            {message ? (
              <Typography color={(theme) => theme.palette.error.main}>
                {message}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ mr: 1 }}
              variant="text"
              size="large"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              disabled={loading || !!message || !amount}
              size="large"
              type="submit"
            >
              {loading ? (
                <CircularProgress sx={{ mx: 1.5 }} size={25} />
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <AlertModal
        open={!!success}
        onClose={() => setSuccess("")}
        title="Success"
        description={success}
        type="success"
      />
      <AlertModal
        open={!!error}
        onClose={() => setError("")}
        title="Error"
        description={error}
        type="error"
      />
    </>
  );
};

export default EnterDealModal;
